/* How many ticks a planned march will take, by the rule the server actually applies.

   The planning card showed `path.length - 1` — cells, not ticks. That is only right for a stack
   whose speed is 1: seven Workers (SPEED 5) sent three cells were promised "ETA: 3" and arrived in
   one. 03/D7 says the mock `/7` cap is dropped and "ETA is computed from D1–D3", and half of that
   was done; this is the other half.

   `selectMovementsForDisplay` already reads `ticks_remaining` from the server for movements in
   flight, and says why deriving it as cells/speed is wrong: water costs double. A *planned* march
   does not exist on the server yet, so it has to be predicted here — and it can be predicted
   exactly, because the client ships the same world matrix the server checks. Absent key = water,
   in both places.

   Mirrors `simulator/use_cases/move_troops.ticks_to_arrive`. The numbers are never written twice:
   speeds and costs come from `GET /game-config/`. */

import { consumptionMultiplier, marchPace } from "src/utils/gameModifiers"
import { activeWorld } from "src/utils/world"

export const isWaterCell = ({ column, row }) => !(`${column}-${row}` in activeWorld().matrix)

const troopConfigFor = (gameConfig, team) =>
  gameConfig?.[team === "human" ? "human_config" : "zombie_config"]?.TROOPS

/* A stack moves at the pace of its slowest member (03/D1), raised by the movement research —
   `move_troops` sets `speed = march_pace(stack_speed(...), ...)`. Leaving the research out here
   would understate the pace of exactly the player who paid to raise it. */
export const speedOfStack = ({ gameConfig, team, troops, race, researchLevels }) => {
  const config = troopConfigFor(gameConfig, team)
  const speeds = Object.entries(troops || {})
    .filter(([, number]) => Number(number) > 0)
    .map(([unit]) => config?.[unit]?.SPEED)
    .filter((speed) => Number.isFinite(speed))
  if (!speeds.length) return null
  return marchPace({ baseSpeed: Math.min(...speeds), gameConfig, team, race, researchLevels })
}

/* One non-exempt unit removes the exemption for everyone (03/D3). */
export const stackIgnoresWater = ({ gameConfig, troops }) => {
  const exempt = gameConfig?.rules_config?.WATER_EXEMPT_UNITS || []
  const units = Object.entries(troops || {})
    .filter(([, number]) => Number(number) > 0)
    .map(([unit]) => unit)
  return units.length > 0 && units.every((unit) => exempt.includes(unit))
}

/* Rounds a pace needs to walk a road from its first cell — `move_troops.rounds_on_path`.
   Deliberately the same loop the server runs: credit the allowance once per tick, then spend it
   cell by cell, carrying the remainder. Points carry over, which is what makes water genuinely
   half speed at every speed rather than only for stacks fast enough to afford a water cell. */
const roundsAtPace = ({ gameConfig, path, speed, ignoresWater }) => {
  const rules = gameConfig?.rules_config || {}
  const landCost = rules.MOVEMENT_COST_LAND ?? 1
  const waterCost = rules.MOVEMENT_COST_WATER ?? 2
  const costOf = (cell) => (ignoresWater || !isWaterCell(cell) ? landCost : waterCost)

  let points = 0
  let index = 0
  let ticks = 0
  const last = path.length - 1
  while (index < last) {
    points += Math.max(1, speed)
    ticks += 1
    while (index < last) {
      const cost = costOf(path[index + 1])
      if (cost > points) break
      points -= cost
      index += 1
    }
    // No stack can be slower than one cell per tick over land; guard against a config that would
    // otherwise spin here forever.
    if (ticks > 1000) return ticks
  }
  return ticks
}

export const etaForPath = ({ gameConfig, team, troops, path, race, researchLevels }) => {
  const speed = speedOfStack({ gameConfig, team, troops, race, researchLevels })
  if (!speed || !path || path.length < 2) return null
  return roundsAtPace({ gameConfig, path, speed, ignoresWater: stackIgnoresWater({ gameConfig, troops }) })
}

/* The movement points a road costs from its first cell — `move_troops.path_points`: one a land
   cell, water double unless the stack is exempt. */
const pathPoints = ({ gameConfig, path, ignoresWater }) => {
  const rules = gameConfig?.rules_config || {}
  const landCost = rules.MOVEMENT_COST_LAND ?? 1
  const waterCost = rules.MOVEMENT_COST_WATER ?? 2
  return path.slice(1).reduce((sum, cell) => sum + (ignoresWater || !isWaterCell(cell) ? landCost : waterCost), 0)
}

/* The food a march prepays at departure — `move_troops.trip_cost` (owner, 2026-10-09): every unit
   pays its upkeep in proportion to the road, `upkeep × points / pace`, at its own pace and with its
   own water exemption (owner, 2026-10-03) — three cells at speed 6 is half a round's upkeep, and a
   multi-round march pays its fraction too. Units are grouped by pace and exemption and each group's
   bill is truncated once, as the server does. Null when there is no road or no force yet. */
export const tripCostForPath = ({ gameConfig, team, troops, path, race, researchLevels }) => {
  if (!path || path.length < 2) return null
  const config = troopConfigFor(gameConfig, team) || {}
  const exempt = gameConfig?.rules_config?.WATER_EXEMPT_UNITS || []
  const multiplier = consumptionMultiplier({ gameConfig, race })
  const upkeepByPace = new Map()
  Object.entries(troops || {})
    .filter(([, number]) => Number(number) > 0)
    .forEach(([unit, number]) => {
      const baseSpeed = config[unit]?.SPEED
      if (!Number.isFinite(baseSpeed)) return
      const pace = Math.max(1, marchPace({ baseSpeed, gameConfig, team, race, researchLevels }))
      const key = `${pace}:${exempt.includes(unit)}`
      const group = upkeepByPace.get(key) || { pace, ignoresWater: exempt.includes(unit), upkeep: 0 }
      group.upkeep += (config[unit]?.CONSUMPTION || 0) * Number(number)
      upkeepByPace.set(key, group)
    })
  if (!upkeepByPace.size) return null
  let total = 0
  upkeepByPace.forEach(({ pace, ignoresWater, upkeep }) => {
    total += Math.trunc((upkeep * multiplier * pathPoints({ gameConfig, path, ignoresWater })) / pace)
  })
  return total
}

/* How many rounds every cell is from one cell, for the stack being planned (owner, 2026-09-29):
   the map outlines the cells one round away while the route is drawn (searched with
   `maxRounds: 1` there).

   It is `etaForPath` run backwards over the whole board. That loop credits the allowance once a
   round and never loses a remainder, so a cell whose road costs C in points is reached on round
   ceil(C / speed) — which makes the question "the cheapest road to every cell", a Dijkstra over
   the same entry costs (land, or water double unless the stack is exempt) on the same cylinder
   the server walks: columns wrap, rows do not. Bounded at `maxRounds`, so a fast stack does not
   search the whole world; cells past the bound are simply absent from the answer.

   The cheapest road is not always the one a click draws — a click off the straight line walks the
   driest route (`maps.findWaypoints`) — so a cell over water may read a round nearer here than the
   road a click gives it. The ETA on the planner is still the order's own. */
export const roundsFromCell = ({ from, speed, landCost = 1, waterCost = 2, ignoresWater = false, maxRounds = 12 }) => {
  // The active world's grid (`utils/world`), read per call: the search is sized to the map it runs on.
  const { columns: COLUMNS, rows: ROWS } = activeWorld()
  const result = new Map()
  const startColumn = Number(from?.col ?? from?.column)
  const startRow = Number(from?.row)
  if (!speed || speed <= 0 || !Number.isFinite(startColumn) || !Number.isFinite(startRow)) return result
  const budget = speed * maxRounds
  const index = (column, row) => row * COLUMNS + column
  const best = new Float64Array(COLUMNS * ROWS).fill(Infinity)
  // A binary heap of [cost, cellIndex]: a few thousand cells at most, so this stays well under a
  // frame and is only paid when the end of the route or the force changes.
  const heap = []
  const push = (item) => {
    heap.push(item)
    let i = heap.length - 1
    while (i > 0) {
      const parent = (i - 1) >> 1
      if (heap[parent][0] <= heap[i][0]) break
      ;[heap[parent], heap[i]] = [heap[i], heap[parent]]
      i = parent
    }
  }
  const pop = () => {
    const top = heap[0]
    const last = heap.pop()
    if (heap.length) {
      heap[0] = last
      let i = 0
      for (;;) {
        const left = 2 * i + 1
        const right = left + 1
        let smallest = i
        if (left < heap.length && heap[left][0] < heap[smallest][0]) smallest = left
        if (right < heap.length && heap[right][0] < heap[smallest][0]) smallest = right
        if (smallest === i) break
        ;[heap[smallest], heap[i]] = [heap[i], heap[smallest]]
        i = smallest
      }
    }
    return top
  }

  const start = index(((startColumn % COLUMNS) + COLUMNS) % COLUMNS, startRow)
  best[start] = 0
  push([0, start])
  while (heap.length) {
    const [cost, at] = pop()
    if (cost > best[at]) continue
    const column = at % COLUMNS
    const row = (at - column) / COLUMNS
    result.set(`${column}-${row}`, Math.ceil(cost / speed))
    for (const [dc, dr] of [
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
    ]) {
      const nextRow = row + dr
      if (nextRow < 0 || nextRow >= ROWS) continue
      const nextColumn = (column + dc + COLUMNS) % COLUMNS
      const step = ignoresWater || !isWaterCell({ column: nextColumn, row: nextRow }) ? landCost : waterCost
      const nextCost = cost + step
      if (nextCost > budget) continue
      const next = index(nextColumn, nextRow)
      if (nextCost < best[next]) {
        best[next] = nextCost
        push([nextCost, next])
      }
    }
  }
  return result
}

/* `roundsFromCell` for the force being drafted, with every number read where `etaForPath` reads
   it. Null when the force has no pace yet (nothing picked). */
export const reachForStack = ({ gameConfig, team, troops, race, researchLevels, from, maxRounds }) => {
  const speed = speedOfStack({ gameConfig, team, troops, race, researchLevels })
  if (!speed || !from) return null
  const rules = gameConfig?.rules_config || {}
  return roundsFromCell({
    from,
    speed,
    landCost: rules.MOVEMENT_COST_LAND ?? 1,
    waterCost: rules.MOVEMENT_COST_WATER ?? 2,
    ignoresWater: stackIgnoresWater({ gameConfig, troops }),
    maxRounds,
  })
}

export default etaForPath
